<template>
    <ModalContent stitch :help-content-id="`popup-producttype-${current_entity_profile}`">
        <template slot="title">{{ title }}</template>
        <template slot="content">
            <h5 class="stitch-modal__title">{{ $translate('informations') }}</h5>
            <v-row>
                <v-col cols="6">
                    <Field
                        v-model="producttype.label"
                        :error-messages="form_errors.name"
                        :label="$translate('name')"
                        name="name"
                        @input="computeProductCode($event)"
                        :autofocus="true"
                        required
                    />
                </v-col>
                <v-col cols="12" sm="6">
                    <Field
                        :value="producttype.code"
                        :error-messages="form_errors.code"
                        :label="$translate('code')"
                        @input="editProductCode"
                        required
                    />
                </v-col>
            </v-row>
            <h5 class="stitch-modal__title">{{ $translate('fields') }}</h5>
            <template v-for="(field, idx) in fields">
                <div class="d-flex mb-3 secondary pa-3" :key="idx">
                    <div class="flex-grow-1">
                        <v-row>
                            <v-col cols="12" sm="4">
                                <Field
                                    v-model="field.label"
                                    :error-messages="
                                        form_errors.fields
                                            ? form_errors.fields[idx].label
                                            : null
                                    "
                                    @input="computeFieldCode(field)"
                                    :label="$translate('label')"
                                    required
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <Field
                                    :value="field.code"
                                    @input="editFieldCode(field, $event)"
                                    :error-messages="
                                        form_errors.fields
                                            ? form_errors.fields[idx].code
                                            : null
                                    "
                                    :disabled="!field.new"
                                    :label="$translate('code')"
                                    required
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <Select
                                    v-model="field.type"
                                    :error-messages="
                                        form_errors.fields
                                            ? form_errors.fields[idx]
                                            : null
                                    "
                                    :options="[
                                        {
                                            value: 'string',
                                            text: $translate('string'),
                                        },
                                        {
                                            value: 'integer',
                                            text: $translate('integer'),
                                        },
                                        {
                                            value: 'float',
                                            text: $translate('float'),
                                        },
                                        {
                                            value: 'boolean',
                                            text: $translate('boolean'),
                                        },
                                        {
                                            value: 'choices',
                                            text: $translate('choices'),
                                        },
                                    ]"
                                    :label="$translate('type')"
                                    required
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" sm="4" v-if="unitNeeded(field)">
                                <Field
                                    v-model="field.unit"
                                    :error-messages="
                                        form_errors.fields
                                            ? form_errors.fields[idx].unit
                                            : null
                                    "
                                    :label="$translate('unit')"
                                />
                            </v-col>
                            <v-col
                                cols="12"
                                class="pt-0"
                                :sm="12"
                                v-if="field.type == 'choices'"
                            >
                                <v-combobox
                                    multiple
                                    v-model="field.choices"
                                    :label="$translate('choices')"
                                    append-icon
                                    chips
                                    deletable-chips
                                    class="tag-input"
                                    @keyup.tab="updateTags(field)"
                                    :search-input.sync="field.choices_string"
                                    @paste="updateTags(field)"
                                    outlined dense>
                                    <template #label>
                                        <span>{{ $translate('choices') }}</span>
                                        <StitchHint :hint="$translate('useTabKeyToValidateEachChoice')" />
                                    </template>
                                </v-combobox>
                            </v-col>
                            <v-col cols="12" class="pt-0" :sm="12">
                                <v-checkbox
                                    v-model="field.filter"
                                    :label="$translate('enableFiltering')"
                                ></v-checkbox>
                            </v-col>
                        </v-row>
                    </div>
                    <div class="d-flex justify-end align-start pt-5 ml-3">
                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                    v-on="on"
                                    v-bind="attrs"
                                    size="20"
                                    @click="deleteField(idx)"
                                    >mdi-delete</v-icon
                                >
                            </template>
                            <span>{{ $translate('deleteField') }}</span>
                        </v-tooltip>
                    </div>
                </div>
            </template>
            <div class="d-flex justify-end mt-0">
                <v-btn @click.prevent="newFieldClicked" small
                    ><v-icon>mdi-plus</v-icon> {{ $translate('addField') }}</v-btn
                >
            </div>
            <template v-if="validFields.length">
                <h5 class="stitch-modal__title">{{ $translate('productDescription') }}</h5>
                <div class="d-flex mb-6 secondary pa-3 pt-5">
                    <div class="flex-grow-1">
                        <v-row>
                            <v-col>
                                <span>{{ $translate('productDescriptionColon') }}</span>&nbsp;
                                <template v-if="descriptionFields.length">
                                    <span
                                        v-for="(field, idx) in descriptionFields"
                                        :key="JSON.stringify(field)"
                                    >
                                        <template v-if="idx">-</template>
                                        <b>
                                            {{ field.label }}
                                        </b>
                                    </span>
                                </template>
                                <i v-else>{{ $translate('addFieldsUsingButtonsBelow') }}</i>
                            </v-col></v-row
                        >
                        <v-row>
                            <v-col cols="12" class="d-flex flex-wrap">
                                <v-btn
                                    class="mr-4 mb-4"
                                    small
                                    v-for="field in validFields"
                                    :key="field.code + '-' + field.label"
                                    @click="toggleFieldInDescription(field)"
                                >
                                    <v-icon
                                        v-if="
                                            (producttype.description || []).indexOf(
                                                field.code
                                            ) == -1
                                        "
                                        >mdi-plus</v-icon
                                    >
                                    <v-icon v-else>mdi-delete</v-icon>

                                    {{ field.label }}
                                </v-btn>
                            </v-col>
                        </v-row>
                    </div>
                </div></template
            >
        </template>
        <template slot="bottom">
            <Button
                :text="$translate('cancel')"
                class="my-2 my-sm-4 w-100 w-sm-auto"
                ghost
                @click="cancelEditClicked"
            />
            <Button
                class="my-2 my-sm-4 w-100 w-sm-auto ml-sm-3"
                :text="save_text"
                @click="saveInformationClicked"
                :loading="producttype_saving"
            />
        </template>
    </ModalContent>
</template>

<script>
import { mapState, mapActions } from "vuex";
import slugify from "slugify";
import GlobalMixins from "@/mixins/global";

export default {
    mixins: [GlobalMixins],
    props: {
        params: {
            type: Object,
            required: true,
        },
    },
    computed: Object.assign(
        mapState("producttype", ["producttype_saving", "producttype_saving_errors"]),
        {
            validFields() {
                return this.producttype.fields.filter(
                    (field) => field.code && field.label
                );
            },
            isNew() {
                return !this.producttype.id;
            },
            save_text() {
                if (this.isNew) {
                    return this.$translate("createProductType");
                }
                return this.$translate("save");
            },
            descriptionFields() {
                if (!this.producttype.description) {
                    return [];
                }
                return this.producttype.description
                    .map((item) =>
                        this.producttype.fields.find((field) => field.code == item)
                    )
                    .filter((item) => item);
            },
            title() {
                return this.params.producttype.id
                    ? this.$translate("editProductType")
                    : this.$translate("createProductType");
            },
        }
    ),
    data() {
        var producttype = JSON.parse(
            JSON.stringify(
                Object.assign(
                    { fields: [], description: [] },
                    this.params.producttype || {}
                )
            )
        );

        let fields = producttype.fields;
        if (!fields.length) {
            fields = [
                {
                    label: null,
                    code: null,
                    type: "string",
                    new: true,
                },
            ];
        }
        producttype.fields = fields;
        return {
            fields,
            form_errors: {},
            producttype: producttype,
            productCodeEdited: Boolean(producttype.code),
            editedFieldCodes: new WeakSet(fields.filter(field => field.code)),
        };
    },
    watch: {
        fields: {
            deep: true,
            handler: function (fields) {
                this.producttype.fields = fields;
            },
        },
    },
    methods: Object.assign(
        mapActions("mcl", ["hideModal"]),
        mapActions("producttype", ["saveProductType"]),
        {
            unitNeeded(field) {
                return field.type == "integer" || field.type == "float";
            },

            toggleFieldInDescription(field) {
                if (!this.producttype.description) {
                    this.producttype.description = [];
                }
                if (this.producttype.description.indexOf(field.code) == -1) {
                    this.producttype.description.push(field.code);
                } else {
                    this.producttype.description = this.producttype.description.filter(
                        (item) => item != field.code
                    );
                }
            },
            updateTags(field) {
                this.$nextTick(() => {
                    if (!field.choices_string) {
                        field.choices_string = "";
                    }
                    let newwords = field.choices_string.split(",").filter((item) => item);
                    if (!field.choices) {
                        field.choices = [];
                    }
                    field.choices.push(...newwords);
                    this.$nextTick(() => {
                        field.choices_string = "";
                    });
                });
            },
            computeProductCode(label) {
                if (this.producttype.id || this.productCodeEdited || !label) {
                    return;
                }
                this.producttype.code = slugify(label.toLowerCase(), "-").replaceAll(
                    "-",
                    ""
                );
            },
            editProductCode(code) {
                if (code === this.producttype.code || (code == null && this.producttype.code == null)) {
                    return;
                }
                this.productCodeEdited = true;
                this.$set(this.producttype, "code", code);
            },
            computeFieldCode(field) {
                if (!field.new || this.editedFieldCodes.has(field) || !field.label) {
                    return;
                }
                this.updateFieldCode(field, slugify(field.label.toLowerCase(), "-").replaceAll("-", ""));
            },
            editFieldCode(field, code) {
                if (code === field.code || (code == null && field.code == null)) {
                    return;
                }
                this.editedFieldCodes.add(field);
                this.updateFieldCode(field, code);
            },
            updateFieldCode(field, code) {
                const previousCode = field.code;
                if (previousCode && this.producttype.description) {
                    this.producttype.description = this.producttype.description.map(
                        item => item === previousCode ? code : item
                    ).filter(Boolean);
                }
                this.$set(field, "code", code);
            },
            deleteField(index) {
                this.fields.splice(index, 1);
            },
            newFieldClicked() {
                this.fields.push({ type: "string", new: true });
            },
            failedSavingProductType(error) {
                this.form_errors = error.details;
                this.$swal({
                    title: this.$translate("error"),
                    html: this.$utils.resultToSwalContent(this, error),
                    type: "error",
                });
            },
            informationsSaved(response) {

            },
            cancelEditClicked() {
                this.hideModal();
            },
            async saveInformationClicked() {
                try {
                    const response = await this.saveProductType({
                        instance: this.producttype,
                    });
                    if (response.status == 201) {
                        this.addAlert(this.$translate("productTypeCreated"));
                    } else {
                        this.addAlert(this.$translate("productTypeUpdated"));
                    }
                    this.hideModal({ result: response.data });
                } catch (error) {
                    this.form_errors = error.details;
                    this.$swal({
                        title: this.$translate("error"),
                        html: this.$utils.resultToSwalContent(this, error),
                        type: "error",
                    });
                }
            },
        }
    ),
    i18n: {
        messages: {
            en: {
                informations: "Informations",
                name: "Name",
                code: "Code",
                fields: "Fields",
                label: "Label",
                type: "Type",
                string: "String",
                integer: "Integer",
                float: "Float",
                boolean: "Boolean",
                choices: "Choices",
                unit: "Unit",
                useTabKeyToValidateEachChoice: "Use TAB key to validate each choice",
                enableFiltering: "Enable filtering",
                deleteField: "Delete field",
                addField: "Add field",
                productDescription: "Product description",
                productDescriptionColon: "Product description:",
                addFieldsUsingButtonsBelow: "Add fields using buttons below",
                cancel: "Cancel",
                createProductType: "Create product type",
                save: "Save",
                editProductType: "Edit a product type",
                error: "Error",
                productTypeCreated: "Product type created",
                productTypeUpdated: "Product type updated",
            },
            fr: {
                informations: "Informations",
                name: "Nom",
                code: "Code",
                fields: "Champs",
                label: "Libellé",
                type: "Type",
                string: "Chaîne",
                integer: "Entier",
                float: "Décimal",
                boolean: "Booléen",
                choices: "Choix",
                unit: "Unité",
                useTabKeyToValidateEachChoice: "Utilisez la touche TAB pour valider chaque choix",
                enableFiltering: "Activer le filtrage",
                deleteField: "Supprimer le champ",
                addField: "Ajouter un champ",
                productDescription: "Description du produit",
                productDescriptionColon: "Description du produit :",
                addFieldsUsingButtonsBelow: "Ajoutez des champs en utilisant les boutons ci-dessous",
                cancel: "Annuler",
                createProductType: "Créer le type de produit",
                save: "Sauvegarder",
                editProductType: "Éditer un type de produit",
                error: "Erreur",
                productTypeCreated: "Type de produit créé",
                productTypeUpdated: "Type de produit mis à jour",
            },
        },
    },
};
</script>
